<template>
  <VDialogV3
    title="Уведомление поставщику"
    position="right"
    width="600px"
    :entity="entity"
    @submit="showApprove"
    :submit-disabled="submitDisabledReason"
  >
    <section>
      <VSwitch
        v-model="downloadOnly"
        theme="slider"
        :checked-value="true"
        :unchecked-value="false"
        checked-label="Скачать файл, не отправлять"
        unchecked-label="Отправить поставщику"
      />
      <VSwitch
        v-if="downloadOnly === false"
        v-model="sendToSellerAndDownload"
        theme="slider"
        :checked-value="true"
        :unchecked-value="false"
        checked-label="Скачать файл"
        unchecked-label="Не скачивать файл"
      />
    </section>

    <section class="grid-block" v-if="downloadOnly === false">
      <header>Контактные лица</header>

      <div class="content">
        <VDataGridV2
          :data="personList"
          class="fill"
          sticky
          selectable
          v-model:selectedRows="selectedPersons"
        >
          <VDataGridColumnV2
            id="name"
            label="Контакт"
            width="200px"
            content-class="ellipsis"
          >
            <template #default="{ row }">
              <div class="name nowrap ellipsis" :title="row.name">
                {{ row.name }}
              </div>
            </template>
          </VDataGridColumnV2>

          <VDataGridColumnV2
            id="email"
            label="E-mail"
            width="200px"
            content-class="ellipsis"
          >
            <template #default="{ row }">
              <div class="email nowrap ellipsis" :title="row.email">
                {{ row.email }}
              </div>
            </template>
          </VDataGridColumnV2>
        </VDataGridV2>

      </div>
    </section>

    <hr>

    <section v-if="showDocumentNumberInput">
      <article>
        <VInput
          v-model="documentNumber"
          label="Номер доп. соглашения"
          layout="vertical"
          placeholder="Введите номер"
        />
      </article>
    </section>
  </VDialogV3>
</template>


<script lang="ts">
import Core from "@/core/Core";
import PartnersService from '@/domains/Partners/PartnersService';
import Utils from "@/core/utils/Utils";
import OrdersService from '@/domains/Orders/OrdersService';
import Order from '@/domains/Orders/Order';
import PreorderPosition from '@/domains/Preorders/PreorderPosition';
import OrderPosition from '@/domains/Orders/OrderPosition';

export default defineComponent({
  name: 'NotifySellerDialog',

  components: {},

  props: {
    /**
     * Кого уведомляем: заказ, его позицию или позицию предзаказа.
     * Предзаказ живёт как документ один раз — дальше работа идёт с позицией,
     * поэтому она приходит сюда сама, а не обёрнутой в Preorder.
     *
     * Поставщик и юрлицо читаются с самой сущности: у позиции они есть в
     * общих реквизитах, поэтому полный объект заказа здесь не нужен.
     */
    entity: {
      type:     Object as PropType<Order | OrderPosition | PreorderPosition>,
      required: true,
    },
  },

  emits: [
    Order.Events.NotificationSent,
    'close',
  ],

  data() {
    return {
      personList:      [],
      selectedPersons: [],
      documentNumber:  null,
      downloadOnly:    false,
      sendToSellerAndDownload: false,
    };
  },

  mounted() {
    this.init();
  },

  watch: {
    /**
     * Второй свитч показывается только в режиме отправки. При переходе в режим
     * «Скачать файл, не отправлять» его значение сбрасывается: иначе на бэк уйдёт
     * флаг, который пользователь уже не видит и выключить не может.
     */
    downloadOnly(value: boolean) {
      if (value === true) {
        this.sendToSellerAndDownload = false;
      }
    },
  },

  computed: {
    /**
     * Причина дизейбла кнопки отправки. null — кнопка активна.
     * Строка показывается как тултип в VDialogV3.
     */
    submitDisabledReason(): string | null {
      if (this.selectedPersons.length === 0 && this.downloadOnly === false) {
        return 'Выберите хотя бы одно контактное лицо';
      }
      else {
        return null;
      }
    },

    /**
     * Поле «Номер доп. соглашения» нужно только для конкретной пары компания/партнёр.
     */
    showDocumentNumberInput(): boolean {
      if (this.entity.sellerCompany.id === 457 && this.entity.seller.id === 504) {
        return true;
      }

      return false;
    },

    /** Заголовок подтверждения: чем уведомление вызвано. */
    confirmTitle(): string {
      if (this.entity instanceof PreorderPosition) {
        return `Предзаказ № ${Utils.string.formatWithDelimiter(this.entity.id)}`;
      }
      else if (this.entity instanceof OrderPosition) {
        return `Заказ № ${Utils.string.formatWithDelimiter(this.entity.order!.id)}`;
      }
      else {
        return `Заказ № ${Utils.string.formatWithDelimiter(this.entity.id)}`;
      }
    },
  },

  methods: {
    async init() {
      const partner = await PartnersService.get(this.entity.seller.id);

      if (partner) {
        this.personList = partner.persons || [];
      }
    },

    async showApprove() {
      const count = this.selectedPersons.length;

      const label = count > 1 ? 'Уведомить выбранных поставщиков?' : 'Уведомить выбранного поставщика?';

      if (this.downloadOnly === false) {
        const confirmed = await Core.confirm(label, this.confirmTitle);
        if (!confirmed) {
          return;
        }
      }

      let ok: boolean;

      /** Бэк ждёт либо заказ, либо позицию предзаказа — позиция заказа
       *  уведомляется через свой заказ. */
      if (this.entity instanceof PreorderPosition) {
        ok = await OrdersService.sendNotification(undefined, this.entity.id, this.selectedPersons, { documentNumber: this.documentNumber }, this.downloadOnly, this.sendToSellerAndDownload);
      } else if (this.entity instanceof OrderPosition) {
        ok = await OrdersService.sendNotification(this.entity.order!.id, undefined, this.selectedPersons, { documentNumber: this.documentNumber },  this.downloadOnly, this.sendToSellerAndDownload);
      } else {
        ok = await OrdersService.sendNotification(this.entity.id, undefined, this.selectedPersons, { documentNumber: this.documentNumber }, this.downloadOnly, this.sendToSellerAndDownload);
      }

      if (ok === true) {
        if (this.downloadOnly === false) {
          // файл, если его запросили вторым свитчем, пришёл прямо в ответе
          // и уже скачан в OrdersService
          this.$emit(Order.Events.NotificationSent);
        }

        this.$core.dialogs.NotifySellerDialog.hide();
      }
    },
  },
});
</script>
